/**
 * Breeding — dedicated calculator page.
 *
 * Lives at /breeding so it benefits from the global fan-art background
 * (Pal Game uses the Chillet Coin variant — different theme). Source
 * of truth for the pal list is the public Paldex (/api/paldex), not
 * the user's box, so all 448 paldex pals are pickable. We filter out
 * (Alpha) variants and the literal tower-boss "<Trainer> & <Pal>"
 * entries — they don't have BreedingPower in gamedata anyway.
 *
 * The calc itself reuses the existing `GET /api/palgame/breed` endpoint.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader, RarityBadge } from "@/components/Atoms";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { Search, Sparkles, ArrowLeft, Filter } from "lucide-react";

// Canonical element list — mirrors the keys used by ElementChip.
const ELEMENTS = ["dark", "dragon", "electric", "fire", "grass", "ground", "ice", "neutral", "water"];

function isTowerBossEntry(name) {
  return typeof name === "string" && name.includes(" & ");
}

export default function Breeding() {
  const toast = useToast();
  const [pals, setPals] = useState([]);          // full filtered paldex roster
  const [loading, setLoading] = useState(true);
  const [p1, setP1] = useState(null);            // selected pal objects
  const [p2, setP2] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);      // community recent breeds

  useEffect(() => {
    // Pull the whole paldex (no pagination — 448 entries is tiny).
    api.get("/paldex?limit=1000")
      .then(({ data }) => {
        // Filter out (Alpha) and the tower-boss "<Trainer> & <Pal>" entries
        // since neither participates in the breeding chart.
        const filtered = (data.results || []).filter(
          (p) => !/\(alpha\)/i.test(p.name) && !isTowerBossEntry(p.name)
        );
        // Stable alpha-sort for the picker UI.
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        setPals(filtered);
      })
      .catch((e) => toast.error(apiError(e, "Couldn't load Paldex")))
      .finally(() => setLoading(false));
    // Initial recent-feed pull + poll every 12s for live community signal.
    const loadRecent = () => api.get("/palgame/breed/recent")
      .then(({ data }) => setRecent(data.recent || []))
      .catch(() => {});
    loadRecent();
    const t = setInterval(loadRecent, 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calc = async () => {
    if (!p1 || !p2) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get(
        `/palgame/breed?pal1=${encodeURIComponent(p1.name)}&pal2=${encodeURIComponent(p2.name)}`
      );
      setResult(data);
      // Refresh the feed so the user sees their own lookup land.
      api.get("/palgame/breed/recent").then(({ data: r }) => setRecent(r.recent || [])).catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const swap = () => {
    setP1(p2);
    setP2(p1);
    setResult(null);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 fade-up" data-testid="breeding-page">
      <PageHeader
        title="Breeding Calculator"
        badge="Beta"
        subtitle="Preview offspring from any two pals in the Paldex. Free — no skulls required."
        right={
          <Link to="/palgame" className="btn btn-sm btn-secondary" data-testid="breeding-back-palgame">
            <ArrowLeft size={12} /> Back to Pal Game
          </Link>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Parent 1 column */}
        <div className="glass-card p-5">
          <h3 className="label mb-3">Parent 1</h3>
          <PalSearchPicker
            value={p1}
            onChange={(p) => { setP1(p); setResult(null); }}
            pals={pals}
            loading={loading}
            testid="breeding-p1"
          />
        </div>

        {/* Result middle */}
        <div className="glass-card p-5 flex flex-col items-center justify-center min-h-[360px]">
          {result ? (
            <div
              className={`text-center w-full rarity-${(result.result_rarity || "common").toLowerCase()}`}
              data-testid="breeding-result"
            >
              <div className="text-xs uppercase tracking-[0.22em] mb-2 text-[var(--muted)]">
                Predicted Offspring
              </div>
              <PalPortrait name={result.result} size="w-32 h-32 mx-auto mb-3" />
              <Link
                to={`/paldex/${result.result.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className="text-2xl sm:text-3xl glow-teal hover:underline underline-offset-4"
                style={{ fontFamily: "'Cinzel Decorative', serif" }}
                data-testid="breeding-result-name"
              >
                {result.result}
              </Link>
              <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
                <RarityBadge rarity={result.result_rarity} />
                {result.special && (
                  <span
                    className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full border border-[var(--gold)]/60 text-[var(--gold)] bg-[rgba(255,215,128,0.06)]"
                    data-testid="breeding-special-combo-badge"
                  >
                    ★ Special Combo
                  </span>
                )}
                {(result.result_elements || []).map((e) => (
                  <ElementChip key={e} element={e} size={14} />
                ))}
              </div>
              <div className="text-[11px] text-[var(--muted)] mt-3 font-mono">
                {result.formula}
              </div>
            </div>
          ) : (
            <div className="text-center w-full">
              <Sparkles size={36} className="text-[var(--gold)] mx-auto mb-3 opacity-70" />
              <p className="text-sm text-[var(--muted)] mb-4">
                Pick two pals to see the predicted offspring.
              </p>
              <button
                onClick={calc}
                disabled={busy || !p1 || !p2}
                className="btn"
                data-testid="breeding-calc-button"
              >
                {busy ? "Calculating…" : "Calculate"}
              </button>
              {p1 && p2 && (
                <button
                  onClick={swap}
                  className="btn btn-sm btn-secondary mt-2 ml-2"
                  data-testid="breeding-swap-button"
                  title="Swap parents"
                >
                  ⇄ Swap
                </button>
              )}
            </div>
          )}

          {result && (
            <button
              onClick={() => setResult(null)}
              className="btn btn-sm btn-secondary mt-4"
              data-testid="breeding-clear-button"
            >
              Try Another Pair
            </button>
          )}
        </div>

        {/* Parent 2 column */}
        <div className="glass-card p-5">
          <h3 className="label mb-3">Parent 2</h3>
          <PalSearchPicker
            value={p2}
            onChange={(p) => { setP2(p); setResult(null); }}
            pals={pals}
            loading={loading}
            testid="breeding-p2"
          />
        </div>
      </div>

      <p className="text-[11px] text-[var(--muted)] mt-6 text-center">
        Powered by the public Paldex. Tower-boss encounters (e.g. <em>Zoe &amp; Grizzbolt</em>) and Alpha variants are excluded — they aren't part of the breeding chart in-game.
      </p>

      <RecentOffspringFeed recent={recent} />
    </div>
  );
}

/**
 * RecentOffspringFeed — community ticker showing the last 5 breed-calculator
 * lookups across all users. Polls /api/palgame/breed/recent every 12s.
 * Pure social signal — surfaces non-obvious combos and gives the page
 * a sense of life when other users are exploring.
 */
function RecentOffspringFeed({ recent }) {
  if (!recent || recent.length === 0) return null;
  return (
    <section className="mt-8" data-testid="breeding-recent-feed">
      <h3 className="label mb-3 flex items-center gap-2">
        Recent Offspring
        <span className="text-[10px] text-[var(--muted)] normal-case tracking-normal">
          · last {recent.length} community lookups
        </span>
      </h3>
      <ul className="space-y-2">
        {recent.map((r, i) => (
          <li
            key={`${r.at}-${i}`}
            className="glass-card p-3 flex items-center gap-3"
            data-testid={`breeding-recent-row-${i}`}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-10 h-10 flex-shrink-0">
                <PalPortrait name={r.parent1} size="w-10 h-10" />
              </div>
              <span className="text-[var(--muted)]">×</span>
              <div className="w-10 h-10 flex-shrink-0">
                <PalPortrait name={r.parent2} size="w-10 h-10" />
              </div>
              <span className="text-[var(--muted)]">→</span>
              <div className="w-10 h-10 flex-shrink-0">
                <PalPortrait name={r.result} size="w-10 h-10" />
              </div>
              <div className="min-w-0 ml-1">
                <div className="text-xs truncate">
                  <span className="text-[var(--teal)]">{r.user_name}</span>{" "}
                  bred{" "}
                  <span className="text-[var(--bone)]">{r.parent1}</span>{" "}
                  <span className="text-[var(--muted)]">×</span>{" "}
                  <span className="text-[var(--bone)]">{r.parent2}</span>{" "}
                  <span className="text-[var(--muted)]">→</span>{" "}
                  <span className="glow-teal font-semibold">{r.result}</span>
                  {r.special && (
                    <span className="text-[var(--gold)] ml-1">★</span>
                  )}
                </div>
                <div className="text-[10px] text-[var(--muted)] mt-0.5">
                  {timeAgo(r.at)}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function timeAgo(iso) {
  const t = new Date(iso).getTime();
  const delta = Math.max(0, Date.now() - t);
  const sec = Math.floor(delta / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

/**
 * Searchable pal picker with portrait + element chips + element filter.
 * Renders a scrolling grid below the search box; clicking a tile selects it.
 */
function PalSearchPicker({ value, onChange, pals, loading, testid }) {
  const [q, setQ] = useState("");
  const [elementFilter, setElementFilter] = useState(null);  // null = all
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let pool = pals;
    if (elementFilter) {
      pool = pool.filter((p) =>
        (p.elements || []).some((e) => e.toLowerCase() === elementFilter)
      );
    }
    if (!needle) return pool;
    return pool.filter((p) =>
      p.name.toLowerCase().includes(needle) ||
      (p.elements || []).some((e) => e.toLowerCase().includes(needle))
    );
  }, [pals, q, elementFilter]);

  return (
    <div className="space-y-3" data-testid={testid}>
      {/* Selected preview */}
      {value ? (
        <div className="flex items-center gap-3 p-2 rounded-md border border-[var(--teal)]/30 bg-[rgba(0,229,255,0.04)]">
          <div className="w-14 h-14 flex-shrink-0">
            <PalPortrait name={value.name} size="w-14 h-14" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{value.name}</div>
            <div className="flex items-center gap-1 flex-wrap mt-0.5">
              <RarityBadge rarity={rarityToTier(value.rarity)} />
              {(value.elements || []).slice(0, 2).map((e) => (
                <ElementChip key={e} element={e} size={11} showLabel={false} />
              ))}
            </div>
          </div>
          <button
            onClick={() => onChange(null)}
            className="text-[10px] uppercase tracking-wider text-[var(--muted)] hover:text-[var(--bone)]"
            data-testid={`${testid}-clear`}
          >
            Clear
          </button>
        </div>
      ) : (
        <div className="text-xs text-[var(--muted)] italic">No pal selected.</div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <input
          className="input pl-8"
          placeholder={loading ? "Loading Paldex…" : "Search pals…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={loading}
          data-testid={`${testid}-search`}
        />
      </div>

      {/* Element filter chips — click to narrow the grid by element. */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testid}-element-filters`}>
        <Filter size={11} className="text-[var(--muted)]" />
        <button
          onClick={() => setElementFilter(null)}
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
            elementFilter === null
              ? "border-[var(--teal)] text-[var(--teal)] bg-[rgba(0,229,255,0.06)]"
              : "border-white/10 text-[var(--muted)] hover:text-[var(--bone)]"
          }`}
          data-testid={`${testid}-element-all`}
        >
          All
        </button>
        {ELEMENTS.map((el) => {
          const active = elementFilter === el;
          return (
            <button
              key={el}
              onClick={() => setElementFilter(active ? null : el)}
              className={`p-0.5 rounded-full border transition-colors ${
                active
                  ? "border-[var(--teal)] bg-[rgba(0,229,255,0.06)]"
                  : "border-white/10 hover:bg-white/5 opacity-70 hover:opacity-100"
              }`}
              title={el}
              data-testid={`${testid}-element-${el}`}
            >
              <ElementChip element={el} size={14} showLabel={false} />
            </button>
          );
        })}
      </div>

      {/* Scrolling grid — 3 cols on small phones (≤sm), 4 on tablets,
          5 on desktop with a comfortable gap so pal portraits don't
          run into each other. Previous 6-col layout at lg was too
          tight — cards read as a solid strip instead of a grid. */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[360px] overflow-y-auto pr-1">
        {filtered.slice(0, 240).map((p) => {
          const active = value?.name === p.name;
          const portraitSrc = p.image && p.image.startsWith("http")
            ? p.image
            : p.image
              ? `${process.env.REACT_APP_BACKEND_URL}${p.image}`
              : null;
          return (
            <button
              key={p.slug || p.name}
              onClick={() => onChange(p)}
              className={`rounded-md p-2.5 text-center transition-colors border ${
                active
                  ? "border-[var(--teal)] bg-[rgba(0,229,255,0.08)]"
                  : "border-white/10 hover:bg-white/5 hover:border-white/20"
              }`}
              data-testid={`${testid}-tile-${p.slug || p.name.toLowerCase().replace(/\s+/g, "-")}`}
              title={p.name}
            >
              <div className="w-12 h-12 mx-auto mb-1.5 rounded overflow-hidden bg-black/30 flex items-center justify-center">
                {portraitSrc ? (
                  <img
                    src={portraitSrc}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
                  />
                ) : (
                  <PalPortrait name={p.name} size="w-12 h-12" />
                )}
              </div>
              <div className="text-[10px] truncate font-medium">{p.name}</div>
            </button>
          );
        })}
        {filtered.length > 240 && (
          <div className="col-span-3 text-center text-[10px] text-[var(--muted)] py-2">
            Showing 240 of {filtered.length} — refine your search.
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="col-span-3 text-center text-xs text-[var(--muted)] py-4">
            No pals match "{q}".
          </div>
        )}
      </div>
    </div>
  );
}

// Convert numeric rarity to RarityBadge's expected tier label.
function rarityToTier(r) {
  if (r >= 15) return "Legendary";
  if (r >= 10) return "Epic";
  if (r >= 7) return "Rare";
  if (r >= 4) return "Uncommon";
  return "Common";
}
